import {inject, Injectable, Injector} from "@angular/core"
import {DataResolver, ResolvedTextureRevision, ResolvedTextureSetRevision} from "@cm/material-nodes"
import {ConfiguratorResolveHdriGQL, ConfiguratorResolveHdriLegacyGQL} from "@app/material-editor/services/configurator-hdri-resolver.generated"
import {contentTypeForExtension, DataObjectWithRelated, mediaTypeSchema} from "@cm/utils"
import {z} from "zod"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {TextureType} from "@generated"
import {ConfiguratorDataObjectLegacyBatchApiCall} from "@app/material-editor/services/configurator-data-object-legacy-batch-api-call"
import {ConfiguratorDataObjectBatchApiCall} from "@app/material-editor/services/configurator-data-object-batch-api-call"
import {ConfiguratorTextureRevisionBatchApiCall} from "@app/material-editor/services/configurator-texture-revision-batch-api-call"
import {ConfiguratorTextureRevisionLegacyBatchApiCall} from "@app/material-editor/services/configurator-texture-revision-legacy-batch-api-call"
import {ConfiguratorTextureSetRevisionBatchApiCall} from "@app/material-editor/services/configurator-texture-set-revision-batch-api-call"
import {ConfiguratorResolvedDataObjectFragment} from "@app/material-editor/services/configurator-data-object-batch-api-call.generated"
import {AsyncCacheMap} from "@app/common/helpers/async-cache-map/async-cache-map"
import {firstValueFrom, from, map} from "rxjs"

const getDataObjectResult = (dataObject: ConfiguratorResolvedDataObjectFragment): DataObjectWithRelated => {
    const getMediaType = (dataObject: Pick<ConfiguratorResolvedDataObjectFragment, "id" | "mediaType" | "originalFileName">) => {
        const {mediaType, originalFileName} = dataObject
        const result = mediaTypeSchema.safeParse(mediaType)
        if (result.success) return result.data
        else {
            const extension = originalFileName.split(".").pop()
            if (!extension) throw Error(`Data object ${dataObject.id} has invalid media type ${mediaType}`)
            return contentTypeForExtension(extension)
        }
    }

    return {
        downloadUrl: dataObject.downloadUrl,
        height: dataObject.height ?? undefined,
        id: dataObject.id,
        imageColorSpace: dataObject.imageColorSpace ?? undefined,
        legacyId: dataObject.legacyId,
        originalFileName: dataObject.originalFileName,
        mediaType: getMediaType(dataObject),
        width: dataObject.width ?? undefined,
        size: dataObject.size,
        imageTilesDescriptor: dataObject.imageTilesDescriptor
            ? {
                  imageWidth: dataObject.imageTilesDescriptor.imageWidth,
                  imageHeight: dataObject.imageTilesDescriptor.imageHeight,
                  tileWidth: dataObject.imageTilesDescriptor.tileWidth,
                  tileHeight: dataObject.imageTilesDescriptor.tileHeight,
                  tileChannels: dataObject.imageTilesDescriptor.tileChannels ?? 3,
                  tileMargin: dataObject.imageTilesDescriptor.tileMargin,
                  tileMipLevels: dataObject.imageTilesDescriptor.tileMipLevels,
                  tileMediaType: mediaTypeSchema.parse(dataObject.imageTilesDescriptor.tileMediaType),
                  tileColorSpace: dataObject.imageTilesDescriptor.tileColorSpace,
                  tileDownloadUrlPrefix: dataObject.imageTilesDescriptor.tileDownloadUrlPrefix,
              }
            : undefined,
        related: dataObject.related.map((related) => ({
            downloadUrl: related.downloadUrl,
            height: related.height ?? undefined,
            id: related.id,
            imageColorSpace: related.imageColorSpace ?? undefined,
            legacyId: related.legacyId,
            originalFileName: related.originalFileName,
            mediaType: getMediaType(related),
            width: related.width ?? undefined,
            size: related.size,
        })),
    }
}

class DataObjectCache extends AsyncCacheMap<number | string, DataObjectWithRelated> {
    private resolveDataObject: ConfiguratorDataObjectBatchApiCall
    private resolveDataObjectLegacy: ConfiguratorDataObjectLegacyBatchApiCall

    constructor(injector: Injector) {
        super((dataObjectId: number | string) => {
            return from(
                typeof dataObjectId === "number"
                    ? this.resolveDataObjectLegacy.fetch({legacyId: dataObjectId})
                    : this.resolveDataObject.fetch({id: dataObjectId}),
            ).pipe(map((dataObject) => getDataObjectResult(dataObject)))
        })

        this.resolveDataObject = new ConfiguratorDataObjectBatchApiCall(injector)
        this.resolveDataObjectLegacy = new ConfiguratorDataObjectLegacyBatchApiCall(injector)
    }
}

class HdriObjectCache extends AsyncCacheMap<number | string, DataObjectWithRelated> {
    private resolveHdri: ConfiguratorResolveHdriGQL
    private resolveHdriLegacy: ConfiguratorResolveHdriLegacyGQL

    constructor() {
        super((hdriId: number | string) => {
            return from(
                typeof hdriId === "number"
                    ? fetchThrowingErrors(this.resolveHdriLegacy)({legacyId: hdriId})
                    : fetchThrowingErrors(this.resolveHdri)({id: hdriId}),
            ).pipe(
                map((result) => {
                    const dataObject = result.configuratorHdri
                    if (!dataObject) throw new Error(`HDRI with ID ${hdriId} does not have a data object associated`)
                    return getDataObjectResult(dataObject)
                }),
            )
        })
        this.resolveHdri = inject(ConfiguratorResolveHdriGQL)
        this.resolveHdriLegacy = inject(ConfiguratorResolveHdriLegacyGQL)
    }
}

class TextureRevisionCache extends AsyncCacheMap<number | string, ResolvedTextureRevision> {
    private resolveTextureRevision: ConfiguratorTextureRevisionBatchApiCall
    private resolveTextureRevisionLegacy: ConfiguratorTextureRevisionLegacyBatchApiCall

    constructor(injector: Injector) {
        super((textureRevisionId: number | string) => {
            return from(
                typeof textureRevisionId === "number"
                    ? this.resolveTextureRevisionLegacy.fetch({legacyId: textureRevisionId})
                    : this.resolveTextureRevision.fetch({id: textureRevisionId}),
            ).pipe(
                map((textureRevision) => {
                    return {
                        dataObjectId: textureRevision.dataObject.id,
                        dataObjectLegacyId: textureRevision.dataObject.legacyId,
                        displacementCm: textureRevision.displacement ?? undefined,
                        heightCm: textureRevision.height,
                        id: textureRevision.id,
                        legacyId: textureRevision.legacyId,
                        textureType: z.nativeEnum(TextureType).parse(textureRevision.texture?.type),
                        widthCm: textureRevision.width,
                    }
                }),
            )
        })
        this.resolveTextureRevision = new ConfiguratorTextureRevisionBatchApiCall(injector)
        this.resolveTextureRevisionLegacy = new ConfiguratorTextureRevisionLegacyBatchApiCall(injector)
    }
}

class TextureSetRevisionCache extends AsyncCacheMap<string, ResolvedTextureSetRevision> {
    private resolveTextureSetRevision: ConfiguratorTextureSetRevisionBatchApiCall

    constructor(injector: Injector) {
        super((textureSetRevisionId: string) => {
            return from(this.resolveTextureSetRevision.fetch({id: textureSetRevisionId})).pipe(
                map((textureSetRevision) => {
                    return {
                        displacementCm: textureSetRevision.displacement ?? undefined,
                        heightCm: textureSetRevision.height,
                        id: textureSetRevision.id,
                        mapAssignments: textureSetRevision.mapAssignments.map((assignment) => ({
                            id: assignment.dataObject.id,
                            legacyId: assignment.dataObject.legacyId,
                            textureType: assignment.textureType,
                        })),
                        widthCm: textureSetRevision.width,
                    }
                }),
            )
        })
        this.resolveTextureSetRevision = new ConfiguratorTextureSetRevisionBatchApiCall(injector)
    }
}

@Injectable({
    providedIn: "root",
})
export class DataResolverService {
    private injector = inject(Injector)
    private dataObjectCache = new DataObjectCache(this.injector)
    private hdriObjectCache = new HdriObjectCache()
    private textureRevisionCache = new TextureRevisionCache(this.injector)
    private textureSetRevisionCache = new TextureSetRevisionCache(this.injector)

    private async dataObject(id: string) {
        return firstValueFrom(this.dataObjectCache.get(id))
    }

    private async dataObjectLegacy(legacyId: number) {
        return firstValueFrom(this.dataObjectCache.get(legacyId))
    }

    private async hdriObject(id: string) {
        return firstValueFrom(this.hdriObjectCache.get(id))
    }

    private async hdriObjectLegacy(legacyId: number) {
        return firstValueFrom(this.hdriObjectCache.get(legacyId))
    }

    private async textureRevision(id: string) {
        return firstValueFrom(this.textureRevisionCache.get(id))
    }

    private async textureRevisionLegacy(legacyId: number) {
        return firstValueFrom(this.textureRevisionCache.get(legacyId))
    }

    private async textureSetRevision(id: string) {
        return firstValueFrom(this.textureSetRevisionCache.get(id))
    }

    resolve = {
        dataObject: this.dataObject.bind(this),
        dataObjectLegacy: this.dataObjectLegacy.bind(this),
        hdriObject: this.hdriObject.bind(this),
        hdriObjectLegacy: this.hdriObjectLegacy.bind(this),
        textureRevision: this.textureRevision.bind(this),
        textureRevisionLegacy: this.textureRevisionLegacy.bind(this),
        textureSetRevision: this.textureSetRevision.bind(this),
        textureSetRevisionSlot: async ({id, slot}: {id: string; slot: TextureType}) => {
            const textureSetRevision = await this.textureSetRevision(id)
            const {mapAssignments, ...rest} = textureSetRevision
            const textureSetRevisionSlot = mapAssignments.find((assignment) => assignment.textureType === slot)
            if (!textureSetRevisionSlot) {
                throw new Error(`Texture set revision ${id} does not have a slot for texture type ${slot}.`)
            }

            return {
                dataObject: {
                    id: textureSetRevisionSlot.id,
                    legacyId: textureSetRevisionSlot.legacyId,
                },
                textureType: slot,
                ...rest,
            }
        },
    } satisfies DataResolver
}
